import { trackerQueryKeys } from './query-keys';
import TrackerAPI, {
  TrackersResponse,
  TrackerType,
} from '@buildwise/libs/trackers/apis/TrackerAPI';
import { AxiosResponse } from 'axios';
import { queryClient } from 'config/queryClient';
import useUserData from 'hooks/useUserData';
import { useMemo } from 'react';
import { useQuery } from 'react-query';

export const useTrackersQuery = (options: { shouldFetch?: boolean } = { shouldFetch: true }) => {
  const { shouldFetch = true } = options;
  const { companyId } = useUserData();

  const { data, ...rest } = useQuery<AxiosResponse<TrackersResponse>, Error>({
    queryKey: trackerQueryKeys.getAllTrackersByCompanyId(companyId),
    queryFn: () => TrackerAPI.getAllTrackers(companyId),
    enabled: !!companyId && shouldFetch,
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
  });

  const trackers = useMemo(() => data?.data?.trackersList || [], [data]);

  const suggestionTracker = useMemo(() => {
    return trackers.find((tracker) => tracker.type === 'SUGGESTION_TRACKER');
  }, [trackers]);

  // Helper to update the cached trackers list after mutation
  const updateTrackersCache = (
    updatedTracker: TrackerType,
    action: 'update' | 'add' | 'delete' = 'update',
  ) => {
    const queryKey = trackerQueryKeys.getAllTrackersByCompanyId(companyId);
    const queryStatus = queryClient.getQueryState(queryKey);

    if (queryStatus?.status && queryStatus.status !== 'idle') {
      queryClient.setQueryData<AxiosResponse<TrackersResponse> | undefined>(queryKey, (old) => {
        if (old) {
          let updatedTrackers: TrackerType[];
          if (action === 'update') {
            updatedTrackers = old.data.trackersList.map((tracker) =>
              tracker.id === updatedTracker.id ? updatedTracker : tracker,
            );
          } else if (action === 'add') {
            updatedTrackers = [updatedTracker, ...(old?.data?.trackersList || [])];
          } else if (action === 'delete') {
            updatedTrackers = old.data.trackersList.filter(
              (tracker) => tracker.id !== updatedTracker.id,
            );
          } else {
            updatedTrackers = old.data.trackersList;
          }
          return {
            ...old,
            data: {
              ...old.data,
              trackersList: updatedTrackers,
            },
          };
        }
        return old;
      });
    }
  };

  return {
    suggestionTracker,
    trackers,
    updateTrackersCache,
    ...rest,
  };
};

export default useTrackersQuery;
